/**
 * ENG-8090: per-agent OCCUPANCY for opencode — "was this agent doing work",
 * which is a different question from both "is the serve up" and "did a turn
 * complete", and the one the busy/idle utilisation pipeline actually asks.
 *
 * ## Why a third signal
 *
 * `agents.last_pane_activity_at` has exactly one writer (the responsiveness
 * probe) and two consumers that want opposite things:
 *
 *   - LIVENESS (`lastAliveMs` in agent-synthetic-probe.ts) wants "a turn
 *     completed". A serve that is logging but not answering must read as dead,
 *     or the wedge class ENG-8058 produced stays invisible.
 *   - UTILISATION + agent-hours billing (`sample_agent_activity()` ->
 *     `agent_activity_samples` -> the admin Utilization tab and
 *     `agent-hours-billing.ts`) wants "the agent was occupied". A serve wedged
 *     mid-turn WAS occupied; it certainly was not idle.
 *
 * For Claude Code one signal happens to serve both, because `pane.log`'s mtime
 * advances continuously while a turn streams and stops when the agent is idle.
 * opencode has no equivalent: its serve writes `opencode-serve.log`, whose mtime
 * advances for HTTP-server reasons that have nothing to do with agent work — and
 * kept advancing throughout ENG-8058's deadlock. Pointing the probe at it would
 * have false-greened liveness AND over-reported busy minutes.
 *
 * So this file tracks occupancy from the turn lifecycle instead, and the two
 * consumers are split at the column level: occupancy lands in
 * `agents.last_busy_activity_at` (read ONLY by the sampler) while turn
 * completion stamps `agents.last_known_good_at` (read by liveness). Nothing this
 * file produces may ever reach `lastAliveMs` — that is the entire point.
 *
 * ## Why an interval, not a timestamp
 *
 * `TurnOutcomeTracker` (ENG-7996) already records when a turn RESOLVED, but a
 * resolution is a point and occupancy is a span. The sampler runs minutely with
 * a 60s freshness window, so a 5-minute turn that only stamps at its end marks
 * ONE bucket busy instead of five — a 5x undercount, which for billing is a 5x
 * under-charge. Reporting age 0 for the whole time a turn is in flight is what
 * makes each of those five samples see a busy agent, matching what Claude
 * Code's continuously-bumped `pane.log` produces for the same work.
 *
 * ## What deliberately does NOT count
 *
 * A `declined` turn — the sender gate refused the message (wrong sender, peer
 * policy). No model call happens, so no work happens. It resolves in
 * milliseconds, so its in-flight window cannot meaningfully inflate a bucket,
 * but stamping it on completion would hold the agent "busy" for a further 60s.
 * On a chatty channel that filters heavily, that alone would manufacture busy
 * minutes out of an idle agent and bill for them.
 *
 * In-memory and per-manager-generation, mirroring `TurnOutcomeTracker`: a
 * restart clears occupancy, which is correct, since a restart also respawns the
 * serve and no turn can still be in flight against the old one.
 */

import { BusyBucketLedger, sharedBusyBuckets } from './busy-bucket-ledger.js';

/** Occupancy state for one agent. */
interface Occupancy {
  /**
   * Turns currently dispatched and not yet resolved. A COUNT, not a boolean:
   * one agent can hold several conversations at once (the bridge keys an
   * opencode session per thread/DM), and a boolean would let the first turn to
   * finish mark the agent idle while the others were still running.
   */
  inFlight: number;
  /** When a counted turn last resolved. Null if none has. */
  lastActiveAt: number | null;
}

export class OpencodeActivityTracker {
  private readonly state = new Map<string, Occupancy>();

  /**
   * ENG-8116: the same lifecycle also drives the duration ledger. opencode is
   * the runtime that can measure occupancy EXACTLY — beginTurn/endTurn bracket
   * real work — so the spans go straight in rather than being inferred from a
   * file mtime the way Claude Code's have to be.
   *
   * Kept inside this class deliberately, rather than having callers poke both:
   * the lifecycle discipline that makes occupancy correct (the `finally`, the
   * live-session guard) already lives at one call site, and a second one to
   * keep in sync is exactly how a leaked in-flight count would bill an idle
   * agent around the clock.
   */
  constructor(private readonly ledger: BusyBucketLedger = sharedBusyBuckets) {}

  private entry(codeName: string): Occupancy {
    let cur = this.state.get(codeName);
    if (!cur) {
      cur = { inFlight: 0, lastActiveAt: null };
      this.state.set(codeName, cur);
    }
    return cur;
  }

  /** A turn has been dispatched to the serve. Call BEFORE awaiting it. */
  beginTurn(codeName: string, now: number = Date.now()): void {
    this.entry(codeName).inFlight += 1;
    // Idempotent while already occupied, so concurrent turns on one agent open
    // the span once and close it when the LAST of them resolves.
    this.ledger.open(codeName, now);
  }

  /**
   * A dispatched turn has resolved. Call in a `finally`, so a throw cannot
   * strand the agent permanently "busy" — a leaked in-flight count would pin
   * the age at 0 forever and bill the agent around the clock.
   *
   * `counted` is false for a gate decline: it occupied the agent for the
   * microseconds the gate took, and nothing more.
   */
  endTurn(codeName: string, counted: boolean, now: number = Date.now()): void {
    const cur = this.entry(codeName);
    // Clamp rather than go negative. An unmatched end is a bug, but a negative
    // count would read as "not busy" while a real turn was still running.
    cur.inFlight = Math.max(0, cur.inFlight - 1);
    if (counted) cur.lastActiveAt = now;
    // Close the occupancy span only when the LAST concurrent turn resolves —
    // closing on the first would stop accruing while the others still run.
    //
    // A declined turn closes the span too, and that is correct: it occupied the
    // agent for the microseconds the gate took, which rounds to zero seconds and
    // is dropped rather than reported. Crediting it as a full bucket is how an
    // idle agent on a heavily-filtered channel would bill for gate refusals.
    if (cur.inFlight === 0) this.ledger.close(codeName, now);
  }

  /**
   * Seconds since this agent was last doing work, or null if it never has been
   * in this manager generation.
   *
   * 0 while any turn is in flight — see the interval rationale above. Null is
   * "no signal", NOT "idle": the API omits the field entirely so a mixed-version
   * fleet cannot have a silent old CLI read as a busy agent (or vice versa).
   */
  activityAgeSeconds(codeName: string, now: number = Date.now()): number | null {
    const cur = this.state.get(codeName);
    if (!cur) return null;
    if (cur.inFlight > 0) return 0;
    if (cur.lastActiveAt == null) return null;
    return Math.max(0, Math.floor((now - cur.lastActiveAt) / 1000));
  }

  /**
   * Drop all state for an agent, so a fresh serve is not born holding the dead
   * one's in-flight count. Called from the same teardown path that resets turn
   * health.
   */
  reset(codeName: string): void {
    this.state.delete(codeName);
    this.ledger.reset(codeName);
  }
}
